import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { CheckCircle2, Copy, Save, RefreshCw } from 'lucide-react';

export const renderStepFinal = ({
  stepFinalData,
  loading,
  handleCopy,
  handleSaveAndExit,
  regenerateFinalStep,
  navigate
}) => {
  if (!stepFinalData) return null;

  return (
    <Card className="max-w-5xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl">🎉 Paso Final: Prompts de Música y Voz</CardTitle>
            <CardDescription>
              Especificaciones ultra-detalladas para audio profesional
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSaveAndExit}>
              <Save className="w-4 h-4 mr-2" />
              Guardar
            </Button>
            <Button variant="outline" size="sm" onClick={regenerateFinalStep} disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Regenerar
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Music Prompt */}
          <Card className="border-2 border-green-200">
            <CardHeader className="bg-green-50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">🎵 Music Prompt</CardTitle>
                  <CardDescription>
                    {stepFinalData.music_prompt.length} caracteres • Especificaciones completas
                  </CardDescription>
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleCopy(stepFinalData.music_prompt)}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div className="bg-white p-4 rounded border max-h-96 overflow-y-auto">
                <p className="text-sm font-mono text-gray-800 whitespace-pre-wrap">
                  {stepFinalData.music_prompt}
                </p>
              </div>

              {/* Music Reference Tracks */}
              {stepFinalData.music_reference_tracks && stepFinalData.music_reference_tracks.length > 0 && (
                <div className="bg-green-50 p-3 rounded">
                  <p className="text-xs font-semibold text-green-900 mb-2">🎧 Reference Tracks:</p>
                  <ul className="space-y-1">
                    {stepFinalData.music_reference_tracks.map((track, idx) => (
                      <li key={idx} className="text-xs text-green-800">• {track}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Music Technical Params */}
              {stepFinalData.music_technical_params && (
                <div className="bg-emerald-50 p-3 rounded">
                  <p className="text-xs font-semibold text-emerald-900 mb-2">⚙️ Technical Specifications:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-emerald-800">
                    {Object.entries(stepFinalData.music_technical_params).map(([key, value]) => (
                      <div key={key}><strong>{key}:</strong> {String(value)}</div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Voice Prompt */}
          <Card className="border-2 border-orange-200">
            <CardHeader className="bg-orange-50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">🎤 Voice Prompt</CardTitle>
                  <CardDescription>
                    {stepFinalData.voice_prompt.length} caracteres • Dirección vocal completa
                  </CardDescription>
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleCopy(stepFinalData.voice_prompt)}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div className="bg-white p-4 rounded border max-h-96 overflow-y-auto">
                <p className="text-sm font-mono text-gray-800 whitespace-pre-wrap">
                  {stepFinalData.voice_prompt}
                </p>
              </div>

              {/* Voice Reference */}
              {stepFinalData.voice_reference && (
                <div className="bg-orange-50 p-3 rounded">
                  <p className="text-xs font-semibold text-orange-900 mb-2">🎭 Reference Voice:</p>
                  <p className="text-xs text-orange-800">{stepFinalData.voice_reference}</p>
                </div>
              )}

              {/* Voice Technical Params */}
              {stepFinalData.voice_technical_params && (
                <div className="bg-amber-50 p-3 rounded">
                  <p className="text-xs font-semibold text-amber-900 mb-2">⚙️ Technical Specifications:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-amber-800">
                    {Object.entries(stepFinalData.voice_technical_params).map(([key, value]) => (
                      <div key={key}><strong>{key}:</strong> {String(value)}</div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Post-Production Notes */}
          {stepFinalData.post_production_notes && stepFinalData.post_production_notes.length > 0 && (
            <Card className="border-2 border-indigo-200">
              <CardHeader className="bg-indigo-50">
                <CardTitle className="text-lg">🎚️ Post-Production Notes</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <ul className="space-y-2">
                  {stepFinalData.post_production_notes.map((note, idx) => (
                    <li key={idx} className="text-sm text-gray-700 flex">
                      <span className="mr-2">•</span>
                      <span>{note}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Success Message */}
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300">
            <CardContent className="pt-6 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-green-900 mb-2">
                ¡Video Completo - Listos para Producir!
              </h3>
              <p className="text-green-800 mb-2">
                MÓNICA ha generado todos los prompts ultra-profesionales:
              </p>
              <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto my-4 text-left">
                <div className="bg-white p-3 rounded">
                  <p className="text-xs font-semibold text-gray-700 mb-1">✅ Prompts Completados:</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>• Prompts Nano Banana (imágenes)</li>
                    <li>• Prompts Runway Gen-3 (video)</li>
                    <li>• Especificaciones de música</li>
                    <li>• Especificaciones de voz</li>
                  </ul>
                </div>
                <div className="bg-white p-3 rounded">
                  <p className="text-xs font-semibold text-gray-700 mb-1">🎯 Listo para:</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>• Generar imágenes (Nano Banana)</li>
                    <li>• Generar videos (Runway)</li>
                    <li>• Producir música (ElevenLabs)</li>
                    <li>• Grabar narración</li>
                  </ul>
                </div>
              </div>
              <div className="flex gap-3 justify-center mt-4">
                <Button variant="outline" onClick={() => navigate('/dashboard')}>
                  Volver al Dashboard
                </Button>
                <Button onClick={() => window.location.reload()}>
                  Crear Otro Video
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  );
};
